import { useEffect, useState } from 'react';
import { getMergeParamsByMedium, getSnippetNamesRequest } from 'api/TemplateEditAPI';
import { MessageMedium } from 'modules/graphql/types';

/**
 * React hook that returns an array of auto-complete params specific to a given messageMedium
 * (if provided) and formatted for use with TinyMCE
 */

export type AutoCompleteParam = {
  text: string;
  value: string;
};

export type AutoCompleteParamResults = {
  error: unknown | null;
  isLoading: boolean;
  params: AutoCompleteParam[];
};

export const useAutoCompleteParams = (
  canUseAutoCompleteParams: boolean,
  messageMedium?: MessageMedium,
): AutoCompleteParamResults => {
  const [error, setError] = useState<unknown | null>(null);
  const [isLoading, setIsLoading] = useState(canUseAutoCompleteParams);
  const [params, setParams] = useState<AutoCompleteParam[]>([]);

  useEffect(() => {
    if (canUseAutoCompleteParams) {
      Promise.all([getMergeParamsByMedium(messageMedium), getSnippetNamesRequest()])
        .then((values) => {
          const formattedParams = values[0].map((paramName: string) => ({
            text: `${paramName}`,
            value: `{{${paramName}}}`,
          }));
          const formattedSnippets = values[1].map((snippetName: string) => ({
            text: `snippet "${snippetName}"`,
            value: `{{{snippet "${snippetName}"}}}`,
          }));
          setParams([...formattedSnippets, ...formattedParams]);
        })
        .catch((err) => {
          setError(err);
        })
        .finally(() => {
          setIsLoading(false);
        });
    }
  }, []);

  return {
    error,
    isLoading,
    params,
  };
};
